import { EditSubOrganisation } from '@/app/settings/components/kl-organisation-settings/kl-organisation-routes';
import {useSettingsStore} from '@/app/settings/settings-store';
import * as KlipApi from '@/api/klip-api.proxy';
import {computed, defineComponent, onMounted, ref, watch} from 'vue';
import {useKlipApiProxy} from '@/plugins/proxy-client';
import {useRouter} from '@/router/router-vue3';
import {useForm} from 'vee-validate';
import {
    klValidateRules,
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';
import * as yup from 'yup';
import {toTypedSchema} from '@vee-validate/yup';

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlUnaCreateEditKlimzone',
    props: {
        zoneId: { type: String, default: '' },
        organisationId: { type: String, default: '' },
    },
    setup(props) {

        const zoneNameValidationRegex = /^[a-zA-Z0-9\-_ .+!()&ö/:,è|'@\s]*$/;
        const namespaceRegex = /^[0-9a-zA-Z-_]{5,50}$/;
        const schema = yup.object({
            zoneId: yup.string().notRequired(),
            name: yup.string().required().max(90).matches(zoneNameValidationRegex).label('Naam zone'),
            mailDestination: yup.string().required().kl_validKlipEmail().label('Stuur planaanvragen naar e-mail'),
            mailSettings: yup.object(),
            namespace: yup.string().required().matches(namespaceRegex).kl_uniqueNamespace('zoneId').label('Namespace'),
            contactName: yup.string().required().max(100).label('Naam contact'),
            contactPhone: yup.string().required().kl_phone().label('Telefoon'),
            contactEmail: yup.string().required().kl_validKlipEmail().label('Email'),
            contactEmailEmergency: yup.string().notRequired().kl_validKlipEmails().label('Email in geval van noodprocedure'),
            contactMessage: yup.string().notRequired().max(400).label('Boodschap voor de planaanvrager'),
            contactPhoneEmergency: yup.string().required().max(20).kl_phone_with_112().label('Telefoon in geval van nood'),
        });

        const router = useRouter();

        const namespaceUrl: string = import.meta.env.VITE_LINKS_NAMESPACE;
        const overlayUrl: string = import.meta.env.VITE_LINKS_OVERLAY;
        const helpdeskMail: string = import.meta.env.VITE_EMAIL_HELPDESK;

        const form = useForm({
            validationSchema: toTypedSchema(schema),
            initialValues: {
                zoneId: props.zoneId || '',
            }
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);

        const disableSubmit = computed((): boolean => {
            return !form.meta.value.valid || !form.meta.value.dirty;
        });


        const name = fields.name.getValue<string>();
        const mailDestination = fields.mailDestination.getValue<string>();
        const namespace = fields.namespace.getValue<string>();
        const contactName = fields.contactName.getValue<string>();
        const contactPhone = fields.contactPhone.getValue<string>();
        const contactEmail = fields.contactEmail.getValue<string>();
        const contactEmailEmergency = fields.contactEmailEmergency.getValue<string>();
        const contactMessage = fields.contactMessage.getValue<string>();
        const contactPhoneEmergency = fields.contactPhoneEmergency.getValue<string>();

        const mailSettings = ref({
            confirmation: false,
            confirmationExampleUri: '',
            reminder1DayToRespond: false,
            reminder1DayToRespondExampleUri: '',
            uploadImklSuccess: false,
            uploadImklSuccessExampleUri: '',
            uploadImklFailed: false,
            uploadImklFailedExampleUri: '',
            respondedNotInvolved: false,
            respondedNotInvolvedExampleUri: ''
        });

        // update form.meta.value.dirty flag
        watch(mailSettings, (newValue) => { fields.mailSettings.value.value = newValue; }, { deep: true, flush: 'sync' });


        const loading = ref<boolean>(false);
        const isSending = ref<boolean>(false);
        const deletingZone = ref<boolean>(false);
        const deleteButtonFocussed = ref<boolean>(false);
        const deletingGeometry = ref<boolean>(false);
        const addingNewGeometry = ref<boolean>(false);
        const unaDisplayName = ref<string>('');


        const routeEditOrganinsation = computed(() => {
            return EditSubOrganisation;
        });

        const editMode = computed((): boolean => {
            return !!props.zoneId;
        });

        const createMode = computed((): boolean => {
            return !!!props.zoneId;
        });

        const title = computed((): string => {
            if (!!props.zoneId) {
                return 'Bewerk KLIM zone';
            } else {
                return 'Nieuwe KLIM zone';
            }
        });

        const submitButtonLabel = computed((): string => {
            return editMode.value ? 'Bewaar veranderingen' : 'KLIM Zone toevoegen';
        });


        const _submit = async () => {
            isSending.value = true;
            const data = <KlipApi.IUpdateKlimZoneInput | KlipApi.ICreateKlimZoneInput> {
                organisationId: props.organisationId,
                alarmCentralTelephone: contactPhoneEmergency.value,
                contactName: contactName.value,
                contactEmail: contactEmail.value,
                contactTelephone: contactPhone.value,
                extraInformation: contactMessage.value,
                contactEmailEmergency: contactEmailEmergency.value,
                mapRequestEmail: mailDestination.value,
                name: name.value,
                namespace: namespace.value,
                sendConfirmation: mailSettings.value.confirmation,
                sendreminder1DayToRespond: mailSettings.value.reminder1DayToRespond,
                sendrespondedNotInvolved: mailSettings.value.respondedNotInvolved,
                senduploadImklFailed: mailSettings.value.uploadImklFailed,
                senduploadImklSuccess: mailSettings.value.uploadImklSuccess,
            };

            if (editMode.value) {
                const fullData = _updateKlimZone(data);
                await useSettingsStore().updateKlimNamespaceZone(fullData);
            }
            else {
                const fullData = _createKlimZone(data);
                await useSettingsStore().createKlimNamespaceZone(fullData);
            }

            isSending.value = false;
            addingNewGeometry.value = false;

            await router.replace({
                name: routeEditOrganinsation.value.name,
                params: {
                    organisationId: props.organisationId,
                }
            });
        }

        const _createKlimZone = (createZoneInput: KlipApi.ICreateKlimZoneInput): KlipApi.CreateKlimZoneInput => {
            return createZoneInput as KlipApi.CreateKlimZoneInput;
        }

        const _updateKlimZone = (createZoneInput: KlipApi.IUpdateKlimZoneInput): KlipApi.UpdateKlimZoneInput => {
            const klimData = {
                ...createZoneInput,
                id: props.zoneId,
            } as KlipApi.UpdateKlimZoneInput;
            return klimData;
        }


        const _onIdChange = (newZoneId: string) => {
            if (!!newZoneId) {
                loading.value = true;
                useKlipApiProxy().unaSettings_GetUnaZoneEditView(newZoneId, props.organisationId)
                    .then((response) => {
                        if (!response.result.id) {
                            return;
                        }

                        unaDisplayName.value = response.result.unaName;
                        name.value = response.result.zoneName;
                        mailDestination.value = response.result.mapRequestEmail;
                        namespace.value = response.result.namespace;
                        contactName.value = response.result.contactName;
                        contactPhone.value = response.result.contactTelephone
                        contactEmail.value = response.result.contactEmail;
                        contactEmailEmergency.value = response.result.contactEmailEmergency;
                        contactMessage.value = response.result.extraInformation;
                        contactPhoneEmergency.value = response.result.alarmCentralTelephone;

                        mailSettings.value = {
                            confirmation: response.result.confirmation,
                            confirmationExampleUri: response.result.confirmationExampleUri,
                            reminder1DayToRespond: response.result.reminder1DayToRespond,
                            reminder1DayToRespondExampleUri: response.result.reminder1DayToRespondExampleUri,
                            uploadImklFailed: response.result.uploadImklFailed,
                            uploadImklFailedExampleUri: response.result.uploadImklFailedExampleUri,
                            uploadImklSuccess: response.result.uploadImklSuccess,
                            uploadImklSuccessExampleUri: response.result.uploadImklSuccessExampleUri,
                            respondedNotInvolved: response.result.respondedNotInvolved,
                            respondedNotInvolvedExampleUri: response.result.respondedNotInvolvedExampleUri
                        };
                        loading.value = false;
                    }).finally(async () => {
                        form.resetForm({ values: form.values });
                    });
            } else {
                loading.value = true;
                useKlipApiProxy().unaSettings_GetUnaZoneCreateView()
                    .then((response) => {
                        mailSettings.value = {
                            confirmation: true,
                            confirmationExampleUri: response.result.confirmationExampleUri,
                            reminder1DayToRespond: false,
                            reminder1DayToRespondExampleUri: response.result.reminder1DayToRespondExampleUri,
                            uploadImklFailed: false,
                            uploadImklFailedExampleUri: response.result.uploadImklFailedExampleUri,
                            uploadImklSuccess: false,
                            uploadImklSuccessExampleUri: response.result.uploadImklSuccessExampleUri,
                            respondedNotInvolved: false,
                            respondedNotInvolvedExampleUri: response.result.respondedNotInvolvedExampleUri
                        };
                        loading.value = false;
                    });
            }
        }

        watch(
            () => props.zoneId,
            _onIdChange,
            { immediate: true });


        onMounted(() => {
            addingNewGeometry.value = !!createMode.value;
        });

        const _onInvalidSubmit = (submittedValues) => {
            console.log('_onInvalidSubmit', submittedValues);
        }

        const onSubmit = form.handleSubmit(_submit, _onInvalidSubmit);

        return {
            namespaceUrl,
            overlayUrl,
            helpdeskMail,
            zoneNameValidationRegex,
            namespaceRegex,

            name,
            mailDestination,
            namespace,
            mailSettings,
            contactName,
            contactPhone,
            contactEmail,
            contactMessage,
            contactPhoneEmergency,
            contactEmailEmergency,

            loading,
            isSending,
            deletingZone,
            deleteButtonFocussed,
            deletingGeometry,
            addingNewGeometry,
            unaDisplayName,

            routeEditOrganinsation,
            editMode,
            createMode,
            title,
            submitButtonLabel,

            onSubmit,
            disableSubmit,

            fields,
        }
    }
})
